import { useMemo } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { toast } from 'sonner';
import { UserForm } from '@/components/organisms/settings-users/UserForm';
import { useRoles } from '@/hooks/settings/useRoles';
import { useCreateUser } from '@/hooks/settings/useCreateUser';
import { useHasPermission } from '@/hooks/usePermissions';
import { apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';

export const Route = createFileRoute('/_dashboard/dashboard/settings/users/new')({
  component: NewUserSettingsRoute,
});

function NewUserSettingsRoute() {
  const router = useRouter();
  const { data: rolesData } = useRoles();
  const roles = useMemo(() => rolesData?.data ?? [], [rolesData?.data]);
  const canCreate = useHasPermission('users:create');
  const canAddTechnician = useHasPermission('technicians:create');
  const createUser = useCreateUser();

  function close() {
    void router.navigate({ to: '/dashboard/settings/users' });
  }

  if (!canCreate) {
    close();
    return null;
  }

  return (
    <UserForm
      mode="create"
      roles={roles}
      isPending={createUser.isPending}
      canAddTechnician={canAddTechnician}
      onBack={close}
      onSubmit={(values, roleIds, addTechnician) => {
        // Empty password is the "send an invitation email" signal — strip the
        // field entirely so the wire payload matches the optional DTO shape on
        // the backend (and so log redaction doesn't special-case empty strings).
        const trimmedPassword = values.password?.trim() ?? '';
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          ...(trimmedPassword ? { password: trimmedPassword } : {}),
          roleIds: roleIds.length > 0 ? roleIds : undefined,
        };
        createUser.mutate(payload, {
          onSuccess: async (data) => {
            // NOT-294: optionally add the new user to the Team roster, linked.
            if (addTechnician) {
              try {
                await apiPost(operationsClient, schedulerEndpoints.technicians(), {
                  authUserId: data.id,
                  name: `${values.firstName} ${values.lastName}`.trim() || values.email,
                  email: values.email,
                });
                toast.success('Added to the Team as a technician');
              } catch (error) {
                toast.error(error instanceof Error ? error.message : 'Could not add to the Team');
              }
            }
            // Password-set creates (invitationSent === false) land on the new
            // user's detail page — the row may be off-page in the index list,
            // so bouncing there looks like the action vanished. Invite-based
            // creates close back to the list (the email is the next step).
            if (data.invitationSent) {
              close();
            } else {
              void router.navigate({
                to: '/dashboard/settings/users/$id',
                params: { id: data.id },
              });
            }
          },
        });
      }}
    />
  );
}
